import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { Card, SkeletonGroup, Skeleton } from '@/components/ui';
import { OtpForm } from '@/features/auth/OtpForm';
import { useSession } from '@/features/auth/sessionContext';
import { paths } from '@/routes/paths';

interface LocationState {
  from?: string;
}

/**
 * /signin — how a returning customer gets back to their orders.
 *
 * The checkout flow already signs people in at its Details step, but that only
 * helps someone who is part-way through buying something. A customer who paid
 * last week, closed the tab, and now wants to know where their parcel is had no
 * door at all — the tracking page told them to sign in and offered nowhere to do
 * it. This is that door, and it reuses the same OTP form rather than growing a
 * second implementation of it.
 *
 * Already signed in? Go straight through. Arriving here with a session is either
 * a bookmark or a redirect that raced the silent refresh, and in both cases
 * showing a login form to someone who is already logged in is just confusing.
 */
export function SignInPage() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = (location.state as LocationState | null)?.from ?? paths.orders;

  const handleSignedIn = useCallback(() => {
    navigate(returnTo, { replace: true });
  }, [navigate, returnTo]);

  if (session.status === 'loading') {
    return (
      <AppShell title="Sign in">
        <SkeletonGroup label="Checking whether you are already signed in">
          <Skeleton className="h-40 w-full" />
        </SkeletonGroup>
      </AppShell>
    );
  }

  if (session.status === 'authenticated') {
    navigate(returnTo, { replace: true });
    return null;
  }

  return (
    <AppShell
      title="Sign in"
      subtitle="Enter your mobile number and we will text you a code."
      backTo={paths.home}
    >
      <Card>
        <OtpForm onSignedIn={handleSignedIn} />
      </Card>

      <p className="mt-4 text-sm text-muted">
        No password to remember. We use the number you gave when you ordered, so your orders are
        waiting on the other side.
      </p>
    </AppShell>
  );
}
